// Angular and RJX Imports
// =========================================================
import { Component, type OnInit, ViewChild, type OnDestroy, HostListener, Output, EventEmitter } from '@angular/core'
import { cloneDeep } from 'lodash'
// JSON Data
// =========================================================
import $storeIndexJSON from 'src/app/05_ag-grid-configs/01_json-grid-configs/store-index/colDefs-main-store-index.json'
// Prime NG Imports
// =========================================================
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog'
// Ag Grid Imports
// =========================================================
import {
  type GridReadyEvent,
  type GridApi,
  type SelectionChangedEvent,
  type RowDataUpdatedEvent,
  type IDetailCellRendererParams,
  GetRowIdParams,
  type DetailGridInfo,
  RowSelectionOptions,
  type RowSelectedEvent
} from 'ag-grid-community'
// Angular Material Imports
// =========================================================
import { MatMenuTrigger } from '@angular/material/menu'
// Custom Imports
// =========================================================
import { CopyStoreIndexJobModalComponent } from 'src/app/04_modals/store-indexes/copy-store-index-job-modal/copy-store-index-job-modal.component'
import { CustomGridOptions, DefaultGridContextMenu } from 'src/app/05_ag-grid-configs/02_global-settings/grid-options'
import { CreateEditStoreIndexJobModalComponent } from 'src/app/04_modals/store-indexes/create-edit-store-index-job-modal/create-edit-store-index-job-modal.component'
import { OutputGridDataModalComponent } from 'src/app/04_modals/store-indexes/output-grid-data-modal/output-grid-data-modal.component'
import { StoreIndexAPIs } from 'src/app/core/apis/store-index_api-calls'
import { ManagementJobsAPIs } from 'src/app/core/apis/job-management_api-calls'
import { SideBarPanels } from 'src/app/05_ag-grid-configs/02_global-settings/grid-variables'
import { StoreIndex_GenerateGridData } from 'src/app/05_ag-grid-configs/04_generate-colDefs/store-index-colDefs'
import { type AGGrigSavedSettingsInterface } from 'src/app/core/interfaces/ag-grid-saved-settings-interface'
import { ConfirmPopupComponent } from 'src/app/03_shared-components/01_alerts/confirm-popup/confirm-popup.component'
import { TriggerApiCallsService } from 'src/app/core/services/cancel-api-call'
import { FormatKey } from 'src/app/utils/global_functions'
import { GeneralAPIs } from 'src/app/core/apis/general_api-calls'
import { SaveGridState } from 'src/app/05_ag-grid-configs/02_global-settings/grid-functions-general'
import { NavigationStart, Router } from '@angular/router'
import { type ZenObservable } from 'zen-observable-ts'
import { type Subscription } from 'rxjs'

@Component({
  standalone: false,
  selector: 'app-store-index',
  templateUrl: './store-index.component.html',
  styleUrls: ['./store-index.component.scss']
})
export class StoreIndexComponent implements OnInit, OnDestroy {
  private readonly routerSubscription: Subscription
  // The SideBarPanels class is used to create and manage the sidebar panels
  private readonly sideBarPanels: any = SideBarPanels()

  gridName: string = 'store_index_management_view'
  // Ag Grid Configuration
  private readonly customGridOptions: any = {
    ...CustomGridOptions,
    context: {
      componentParent: this,
      allowSaveView: true,
      gridName: this.gridName,
      columnsToAutoSize: ['job_name', 'product_node', 'buying_season', 'status', 'edited', 'last_modified'],
      pageTitle: 'Store Index Management',
      onOpenOutputModal: params => this.onOpenOutputModal(params),
      onOpenEditJobModal: params => this.onOpenCreateEditJobsModal(params, 'edit'),
      providers: [StoreIndexAPIs, ManagementJobsAPIs]
    }
  }

  // Ag Grid Configuration
  gridApi: GridApi
  columnApi: {}
  gridOptions: any = {}
  isAgGridLoading: boolean = true
  detailCellRendererParams: any

  rowsSelected: boolean = false
  selectedJobNames: any[] = []
  selectedMasterRows: any[] = []
  selectedDetailRows: any[] = []
  masterRowId: string = ''
  isSelected: boolean = false

  // Grid Data
  columnDefs: any[] = []

  eventPointer: any
  // Variables to monitor data from the selected grid rows
  selectedStoreIndexes: any[] = []
  public storeIndex = $storeIndexJSON
  storedGridSettings: AGGrigSavedSettingsInterface
  // Enable and disable grid actions based on specific rules
  gridActionsEnabled = {
    approve: false,
    unapprove: false,
    run: false,
    delete: false,
    copy: false,
    viewOutput: false
  }

  @Output() rowsSelectedEvent = new EventEmitter<any>()

  // App sync
  subscription: ZenObservable.Subscription = null

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger
  @ViewChild('confirmPopupDelete') confirmPopupDelete: ConfirmPopupComponent

  constructor (
    private readonly router: Router,
    private ref: DynamicDialogRef,
    public dialogService: DialogService,
    private readonly storeIndexAPIs: StoreIndexAPIs,
    public triggerApiCallsService: TriggerApiCallsService,
    private readonly generalAPIs: GeneralAPIs
  ) {
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.gridApi?.setFilterModel({})
      }
    })
    this.storedGridSettings = JSON.parse(sessionStorage.getItem('ag_grid_settings')) || {}
    //  Set AG Grid Options
    this.gridOptions = {
      ...this.customGridOptions,
      sideBar: {
        toolPanels: [this.sideBarPanels.columnPanel, this.sideBarPanels.filterPanel],
        ...this.sideBarPanels.defaultLayout
      },
      onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
      getRowId: node => FormatKey(node?.data?.job_name),
      // custom context Menu Items
      getContextMenuItems: params => this.storeIndexJobsGridContextMenu(params),
      // onSelectionChanged: (event: SelectionChangedEvent) => this.rowSelectionChanged(event, false),
      onRowDataUpdated: (event: RowDataUpdatedEvent) => {
        event.api.redrawRows()
      },
      isRowMaster: dataItem => {
        // Only allow expansion if there are multiple detail items
        return dataItem.detail_data && dataItem.detail_data.length > 1
      },
      onRowSelected: async event => this.masterRowSelected(event),
      onRowGroupOpened: params => {
        if (params.expanded) {
          const parentNode = params.node
          console.log('parentNode', parentNode, parentNode.isSelected())
          if (parentNode.isSelected()) {
            setTimeout(() => {
              this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
                console.log('info', info)
                if (info.id === `detail_${parentNode.id}`) {
                  info.api.selectAll()
                }
              })
            }, 0)
          }
        }
      }
      // onFirstDataRendered: (event) => {   this.columnApi?.autoSizeColumns(['prior_selling_seasons', 'use_aug_sales', 'vg_count', 'edited', 'last_modified'], true)}
    }

    // Event is fired when the user right-clicks on an element to open the context menu
    document.addEventListener('contextmenu', this.onContextMenu)
  }

  private readonly onContextMenu = (event: MouseEvent) => {
    if (event.button === 2) {
      this.eventPointer = event
      // console.log('Right click', event);
    }
  }

  ngOnInit () {
    // Call getAllStoreIndexes to get the most updated data
    this.getAllStoreIndexes(false)
  }

  onGridReady (event: GridReadyEvent) {
    this.gridApi = event.api
    this.columnApi = event.api
    this.isAgGridLoading = false
    this.gridApi.closeToolPanel()
  }

  // Render AG Grid Column Definitions
  async renderGrid (mainColDefs, detailColDefs, rowData) {
    this.selectedStoreIndexes = []

    const params = {
      mainColDefs,
      detailColDefs
    }
    let gridData
    try {
      await this.getGridState()
      gridData = await StoreIndex_GenerateGridData(params)
    } finally {
      this.columnDefs = gridData.mainColDefs
      console.log('gridData:', gridData.detailColDefs)
      this.gridOptions.masterDetail = true

      this.detailCellRendererParams = {
        detailGridOptions: {
          ...CustomGridOptions,
          rowSelection: {
            mode: 'multiRow',
            enableClickSelection: true,
            checkboxes: false,
            headerCheckbox: false
          },
          context: {
            gridName: this.gridName + ' - detail',
            pageTitle: 'Store Index Management Detail'
          },
          // suppressRowClickSelection: true,
          enableRangeSelection: true,
          pagination: true,
          paginationPageSize: 10,
          paginationPageSizeSelector: false,
          keepDetailRows: true,
          columnDefs: gridData.detailColDefs,
          defaultColDef: {
            flex: 1
          },
          getRowId: params => {
            return `rownode${params.data.index_id}`
          },
          getContextMenuItems: params => this.storeIndexDetailGridContextMenu(params),
          onSelectionChanged: (event: SelectionChangedEvent) => this.rowSelectionChanged(event, true)
        },
        getDetailRowData: params => {
          console.log('Detail data being passed:', params.data.detail_data)
          params.successCallback(params.data.detail_data)
        }
      }

      // Now set to grid options
      this.gridOptions.masterDetail = true
      this.gridOptions.detailCellRendererParams = this.detailCellRendererParams
      this.detailCellRendererParams['detailGridOptions']['rowSelection']['enableClickSelection'] = true

      // function to check if AgGrid is loading
      const checkAgGrid = () => {
        if (!this.isAgGridLoading) {
          if (this.gridApi && !this.gridApi['destroyCalled']) {
            this.gridApi?.setGridOption('columnDefs', gridData.mainColDefs)

            this.gridApi?.setGridOption('rowData', rowData)

            // Set the subscription for APSYNC
            this.subscription?.unsubscribe()
            this.storeIndexAPIs
              .JobGridDataSubscription(this.gridApi)
              .then(sub => (this.subscription = sub))
            // Apply the saved column state
            if (Object.keys(this.storedGridSettings).length > 0 && this.storedGridSettings[this.gridName]) {
              const { colDefs, filters } = this.storedGridSettings[this.gridName]
              // Apply the saved column state
              setTimeout(() => {
                this.gridApi?.applyColumnState({
                  state: colDefs
                })
                // Apply the column order
                this.gridApi?.moveColumns(
                  colDefs.map(col => col.colId),
                  0
                )
                // Apply the saved filter state
                this.gridApi?.setFilterModel(filters)
              }, 100)
            }
          }
          clearInterval(setData)
        } else {
          console.log('Ag grid is loading')
        }
      }
      const setData = setInterval(checkAgGrid, 100)
    }
  }

  // Get grid view
  async getGridState () {
    return await this.generalAPIs.GetGridView(this.gridOptions.context.gridName).then(res => {
      this.storedGridSettings[this.gridOptions.context.gridName] = res?.is_success
        ? res.data
        : {
            colDefs: [],
            filters: []
          }
    })
  }

  // Track Row Selection Changes
  onSelectedStoreIndexChange (event) {
    console.log('row selection: changed: ', this.selectedStoreIndexes)
    const rows = event.api.getSelectedNodes()
    console.log('rows: ', rows)
    const isRowSelected = rows.length > 0
    this.selectedStoreIndexes = rows

    // Reset the status tracker
    this.gridActionsEnabled = {
      approve: false,
      unapprove: false,
      run: false,
      delete: false,
      copy: false,
      viewOutput: false
    }
    if (isRowSelected) {
      const selectedStatuses = {
        generated: false,
        approved: false,
        running: false,
        failed: false,
        pending: false
      }
      // Update Tracked statuses
      rows.forEach((row, i) => {
        // if (row.data) {
        //   const status = row?.data?.status.toLowerCase()
        //   if (!selectedStatuses[status]) {
        //     selectedStatuses[status] = true
        //   }
        // }
        if (rows.length === i + 1) {
          const { running, approved, generated, failed, pending } = selectedStatuses
          this.gridActionsEnabled = {
            approve: !approved && !running && !failed && !pending,
            unapprove: !generated && !running && !failed && !pending,
            run: !running && !approved,
            delete: !running,
            copy: !running && !failed,
            viewOutput: !running && !failed
          }
          console.log('gridActions enabled: ', this.gridActionsEnabled)
        }
      })
    }
  }

  rowSelectionChanged (event: SelectionChangedEvent, wasDetailSelected: boolean) {
    if (wasDetailSelected) {
      this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
        const parentNode = this.gridApi.getRowNode(info.id.slice(7))
        parentNode.setSelected(info.api.getSelectedNodes().length === parentNode.data.detail_data.length)
      })
    } else {
      this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
        const parentNode = this.gridApi.getRowNode(info.id.slice(7))
        if (parentNode.isSelected()) {
          info.api.selectAll('filtered')
        }
      })
    }

    const detailRows: any[] = []
    this.selectedJobNames = []

    const masterRows = this.gridApi.getSelectedRows()
    let detailRowsData = masterRows.reduce((accumulator, item) => {
      const copyRows = item.detail_data.map(row => {
        return { ...row }
      })
      copyRows.forEach(row => {
        row.job_name = item.job_name
      })
      return accumulator.concat(copyRows)
    }, [])

    this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
      const parentId = info.id.slice(7)
      const parentNode = this.gridApi.getRowNode(parentId)
      const parentData = parentNode?.data

      info.api.getSelectedRows().forEach(row => {
        detailRows.push({
          ...row,
          job_name: parentData?.job_name ?? null
        })
      })

      detailRowsData = detailRowsData.concat(
        info.api.getSelectedNodes().map(node => {
          if (!this.selectedJobNames.includes(node.data.job_name)) {
            this.selectedJobNames.push(node.data.job_name)
          }

          return node.data
        })
      )
    })

    // if (
    //   (this.selectedJobNames.length === 1 && this.selectedMasterRows === 0) ||
    //         (this.selectedJobNames.length === 0 && this.selectedMasterRows === 1) ||
    //         (this.selectedJobNames.length === 1 &&
    //             this.selectedMasterRows === 1 &&
    //             masterRows[0]?.job_name === this.selectedJobNames[0])
    // ) {
    //   this.canViewData = true
    //   this.updateCanViewDataStatus.emit(true)
    // } else {
    //   this.canViewData = false
    //   this.updateCanViewDataStatus.emit(false)
    // }
    this.selectedDetailRows = detailRows
    console.log('detail rows', detailRows)
    const selectedStatuses = {
      approved: false,
      completed: false,
      failed: false,
      pending: false,
      running: false
    }

    let allFromSameGroup = true

    if (this.selectedMasterRows.length > 1) {
      allFromSameGroup = false
    } else if (detailRows.length > 0) {
      const firstJobName = detailRows[0].job_name
      allFromSameGroup = detailRows.every(row => row.job_name === firstJobName)
    }

    detailRowsData.forEach(row => {
      selectedStatuses[row.status.toLowerCase()] = true
    })

    console.log('final selected statuses', selectedStatuses)
    const { approved, completed, failed, pending, running } = selectedStatuses

    const editActionsEnabled = !running
    const hasSelection = masterRows.length > 0 || detailRows.length > 0

    this.gridActionsEnabled = {
      ...this.gridActionsEnabled,
      approve: editActionsEnabled && !approved && !failed,
      viewOutput: hasSelection && editActionsEnabled && !failed && !pending && allFromSameGroup,
      unapprove: editActionsEnabled && approved,
      delete: editActionsEnabled,
      copy: editActionsEnabled,
      run: editActionsEnabled && !approved
    }
    console.log('this.gridActionsEnabled', this.gridActionsEnabled)

    this.rowsSelectedEvent.emit({
      data: {
        rows: detailRows,
        ids: detailRows.map(row => row.id),
        wasDetailSelected
      }
    })
  }

  masterRowSelected (event: RowSelectedEvent) {
    this.selectedMasterRows = this.gridApi.getSelectedNodes()

    if (event.event || event.source === 'uiSelectAllFiltered') {
      if (!event.node.isSelected()) {
        this.gridApi.getDetailGridInfo('detail_' + event.node.id)?.api.deselectAll()
      }
      this.rowSelectionChanged(null, false)
    } else {
      console.log('not a real event, ignore')
    }
  }

  // Sets the context menu in the grid
  storeIndexJobsGridContextMenu (params) {
    console.log('---> Viewing Context Menu: ', params)
    console.log('master rows', this.selectedMasterRows)
    if (params.column) {
      const selectedRow = params.api.getSelectedNodes()
      // Certain Actions should be disabled based on the statuses of selected rows
      const { approve, unapprove, run, viewOutput } = this.gridActionsEnabled
      // Approve store index action button
      const approveStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `Approve Job${selectedRow.length === 1 ? '' : 's'}`
                          : 'Approve (No Jobs Selected)',
          action: async () => await this.onApproveJobs(params),
          // disabled: selectedRow.length === 0 || approved || running,
          disabled: !approve || this.selectedMasterRows.length === 0,
          cssClasses: ['text-accent']
        }
      ]
      // Un approve store index action button
      const unApproveStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `Unapprove Job${selectedRow.length === 1 ? '' : 's'}`
                          : 'Unapprove (No Jobs Selected)',
          action: async () => await this.onUnapproveJobs(params),
          // disabled: selectedRow.length === 0 || generated || running,
          disabled: !unapprove || this.selectedMasterRows.length === 0,
          cssClasses: ['text-accent']
        }
      ]
      // Un approve store index action button
      const runStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `Run Job${selectedRow.length === 1 ? '' : 's'}`
                          : 'Run (No Jobs Selected)',
          action: async () => await this.onRunJobs(params),
          // disabled: selectedRow.length === 0 || running || approved,
          disabled: !run || this.selectedMasterRows.length === 0,
          cssClasses: ['text-accent']
        }
      ]
      // View output store index action button
      const viewOutputStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `View Output${selectedRow.length === 1 ? '' : 's'}`
                          : 'View Output (No Jobs Selected)',
          action: async () => this.onOpenOutputModal(null),
          // disabled: selectedRow.length === 0 || running || approved,
          disabled: !viewOutput || this.selectedMasterRows.length === 0,
          cssClasses: ['text-accent']
        }
      ]
      // Delete store index action button
      const deleteStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `Delete Job${selectedRow.length === 1 ? '' : 's'}`
                          : 'Delete (No Jobs Selected)',
          action: async () => await this.confirmPopupDelete.confirm(this.eventPointer, 'Delete Job'),
          // disabled: selectedRow.length === 0 || running,
          disabled: !this.gridActionsEnabled.delete || this.selectedMasterRows.length === 0,
          cssClasses: ['text-warn']
        }
      ]

      const result = [
        ...approveStoreIndexButton,
        ...unApproveStoreIndexButton,
        ...runStoreIndexButton,
        ...viewOutputStoreIndexButton,
        ...deleteStoreIndexButton,
        'separator',
        ...DefaultGridContextMenu(params)
      ]

      return result
    } else {
      return DefaultGridContextMenu(params)
    }
  }

  storeIndexDetailGridContextMenu (params) {
    console.log('---> Viewing Context Menu: ', params)
    if (params.column) {
      const selectedRow = params.api.getSelectedNodes()
      // Certain Actions should be disabled based on the statuses of selected rows
      const { approve, unapprove, run, viewOutput } = this.gridActionsEnabled
      console.log('this.gridActionsEnabled', this.gridActionsEnabled)
      // Approve store index action button
      const approveStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `Approve Index${selectedRow.length === 1 ? '' : 's'}`
                          : 'Approve (No Indexes Selected)',
          action: async () => await this.onApproveJobs(params),
          // disabled: selectedRow.length === 0 || approved || running,
          disabled: !approve || this.selectedDetailRows.length === 0,
          cssClasses: ['text-accent']
        }
      ]
      // Un approve store index action button
      const unApproveStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `Unapprove Index${selectedRow.length === 1 ? '' : 's'}`
                          : 'Unapprove (No Indexes Selected)',
          action: async () => await this.onUnapproveJobs(params),
          // disabled: selectedRow.length === 0 || generated || running,
          disabled: !unapprove || this.selectedDetailRows.length === 0,
          cssClasses: ['text-accent']
        }
      ]
      // // Un approve store index action button
      // const runStoreIndexButton = [
      //   {
      //     name:
      //                   selectedRow.length > 0
      //                     ? `Run Job${selectedRow.length === 1 ? '' : 's'}`
      //                     : 'Run (No Jobs Selected)',
      //     action: async () => await this.onRunJobs(params),
      //     // disabled: selectedRow.length === 0 || running || approved,
      //     disabled: !run || this.selectedDetailRows.length === 0,
      //     cssClasses: ['text-accent']
      //   }
      // ]
      // View output store index action button
      const viewOutputStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `View Output${selectedRow.length === 1 ? '' : 's'}`
                          : 'View Output (No Indexes Selected)',
          action: async () => this.onOpenOutputModal(null),
          // disabled: selectedRow.length === 0 || running || approved,
          disabled: !viewOutput || this.selectedDetailRows.length === 0,
          cssClasses: ['text-accent']
        }
      ]
      const selectedJobNames = this.selectedMasterRows.map(row => row.data?.job_name || row.job_name)
      const uniqueJobNames = new Set(selectedJobNames)
      // Delete store index action button
      const deleteStoreIndexButton = [
        {
          name:
                        selectedRow.length > 0
                          ? `Delete Index${selectedRow.length === 1 ? '' : 's'}`
                          : 'Delete (No Indexes Selected)',
          action: async () => await this.OnDeleteIndexes(params),
          // disabled: selectedRow.length === 0 || running,
          disabled:
                        !this.gridActionsEnabled.delete ||
                        this.selectedDetailRows.length === 0 ||
                        uniqueJobNames.size > 1,
          cssClasses: ['text-warn']
        }
      ]

      const result = [
        ...approveStoreIndexButton,
        ...unApproveStoreIndexButton,
        ...viewOutputStoreIndexButton,
        ...deleteStoreIndexButton,
        'separator',
        ...DefaultGridContextMenu(params)
      ]

      return result
    } else {
      return DefaultGridContextMenu(params)
    }
  }

  // On Approve Jobs
  async onApproveJobs (event) {
    const masterRows = cloneDeep(this.selectedMasterRows)
    const detailRows = cloneDeep(this.selectedDetailRows)
    const index_ids = Array.from(
      new Set([
        ...masterRows.flatMap(row =>
          row.data.detail_data.map(detail => detail.index_id).filter(id => id != null)
        ),
        ...detailRows.map(row => row.index_id).filter(id => id != null)
      ])
    )
    console.log('---> store_indexes to approve: ', index_ids)

    try {
      //  Show loading overlay
      this.gridApi.showLoadingOverlay()
      // Save Grid State
      await SaveGridState(this.gridApi, this.gridOptions, this.gridName)
    } finally {
      // An API call to approve jobs
      await this.storeIndexAPIs.OnApproveJobs({ index_ids }).then(res => {
        if (res?.is_success) {
          console.log('Successfully Approved Distro')
          // Call getAllStoreIndexes to get the most updated data
          this.getAllStoreIndexes(true)
        } else {
          // Hide loading overlay
          this.gridApi.hideOverlay()
        }
      })
    }
  }

  // On Unapprove Jobs
  async onUnapproveJobs (event) {
    const masterRows = cloneDeep(this.selectedMasterRows)
    const detailRows = cloneDeep(this.selectedDetailRows)
    const index_ids = Array.from(
      new Set([
        ...masterRows.flatMap(row =>
          row.data.detail_data.map(detail => detail.index_id).filter(id => id != null)
        ),
        ...detailRows.map(row => row.index_id).filter(id => id != null)
      ])
    )
    console.log('---> store_indexes to unapprove: ', index_ids)

    try {
      //  Show loading overlay
      this.gridApi.showLoadingOverlay()
      // Save Grid State
      await SaveGridState(this.gridApi, this.gridOptions, this.gridName)
    } finally {
      // An API call to unapprove jobs
      await this.storeIndexAPIs.OnUnapproveJobs({ index_ids }).then(res => {
        if (res?.is_success) {
          console.log('Successfully Unapproved Distro')
          // Call getAllStoreIndexes to get the most updated data
          this.getAllStoreIndexes(true)
        } else {
          // Hide loading overlay
          this.gridApi.hideOverlay()
        }
      })
    }
  }

  // On run jobs
  async onRunJobs (event) {
    const masterRows = cloneDeep(this.selectedMasterRows)
    const detailRows = cloneDeep(this.selectedDetailRows)
    const job_names = Array.from(
      new Set([
        ...masterRows.flatMap(row =>
          row.data.detail_data.map(detail => detail.job_name).filter(id => id != null)
        ),
        ...detailRows.map(row => row.job_name).filter(id => id != null)
      ])
    )

    console.log('---> store_indexes to run: ', job_names)

    // Optimistically flip the affected jobs to RUNNING so the user can't re-run
    // jobs that are already running. The GraphQL subscription
    // (JobGridDataSubscription) will overwrite this with the real status.
    this.markJobsRunning(job_names)

    try {
      // Save Grid State
      await SaveGridState(this.gridApi, this.gridOptions, this.gridName)
    } finally {
      // An API call to on run jobs
      await this.storeIndexAPIs.OnRunJobs({ job_names }, true).then(res => {
        if (res?.is_success) {
          this.gridApi.deselectAll()
          this.gridApi.forEachDetailGridInfo(info => info.api.deselectAll())
          this.selectedMasterRows = []
          this.selectedDetailRows = []
        } else {
          this.gridApi.hideOverlay()
        }
      })
    }
  }

  // Optimistically set the given jobs to RUNNING in the master grid and any open
  // detail grids. Mirrors the update shape used by JobGridDataSubscription so the
  // status (and the disabled "Run" action) reflects immediately.
  markJobsRunning (jobNames: string[]) {
    // Update the master rows (and their detail_data / statuses array)
    this.gridApi.forEachNode(rowNode => {
      if (!jobNames.includes(rowNode.data.job_name)) return

      const updatedDetailData = rowNode.data.detail_data.map(detailRow =>
        detailRow.job_name === rowNode.data.job_name ? { ...detailRow, status: 'RUNNING' } : detailRow
      )
      const statuses = updatedDetailData.map(row => row.status)

      this.gridApi.applyTransactionAsync({
        update: [
          {
            ...rowNode.data,
            statuses,
            detail_data: updatedDetailData
          }
        ]
      })
    })

    // Update any open detail grids so the running status shows there too
    this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
      const rowsToUpdate = []
      info.api.forEachNode(node => {
        if (jobNames.includes(node.data.job_name)) {
          rowsToUpdate.push({ ...node.data, status: 'RUNNING' })
        }
      })
      if (rowsToUpdate.length > 0) {
        info.api.applyTransactionAsync({ update: rowsToUpdate })
      }
    })
  }

  async onDeleteJobs (event) {
    console.log('---> Deleting Job: ', this.selectedStoreIndexes)
    this.trigger.closeMenu()
    // Check for the confirm popup
    if (event.userInput === 'except') {
      if (event.command === 'Delete Job') {
        console.log('---> Deleting Job: ', this.selectedMasterRows, this.selectedDetailRows)
        const masterRows = cloneDeep(this.selectedMasterRows)
        const detailRows = cloneDeep(this.selectedDetailRows)
        const store_index_job_names = masterRows.map(row => row.data.job_name)
        const index_ids = Array.from(
          new Set([
            ...masterRows.flatMap(row =>
              row.data.detail_data.map(detail => detail.index_id).filter(id => id != null)
            ),
            ...detailRows.map(row => row.index_id).filter(id => id != null)
          ])
        )
        console.log('---> store_indexes to unapprove: ', store_index_job_names)
        try {
          this.gridApi.showLoadingOverlay()
          // Save Grid State
          await SaveGridState(this.gridApi, this.gridOptions, this.gridName)
        } finally {
          // An API call to delete store index
          await this.storeIndexAPIs.OnDeleteJobs({ store_index_job_names }).then(res => {
            if (res?.is_success) {
              console.log('Successfully Deleted Job')
              // Call getAllStoreIndexes to get the most updated data
              this.getAllStoreIndexes(true)
            } else {
              // Hide loading overlay
              this.gridApi.hideOverlay()
            }
          })
        }
      }
    }
  }

  async OnDeleteIndexes (event) {
    console.log('---> Deleting Job: ', this.selectedStoreIndexes)
    this.trigger.closeMenu()

    const detailRows = cloneDeep(this.selectedDetailRows)
    const index_ids = Array.from(new Set([...detailRows.map(row => row.index_id)]))
    console.log('---> store_indexes to delete: ', index_ids)
    try {
      this.gridApi.showLoadingOverlay()
      // Save Grid State
      await SaveGridState(this.gridApi, this.gridOptions, this.gridName)
    } finally {
      // An API call to delete store index
      await this.storeIndexAPIs.OnDeleteIndexes({ index_ids }).then(res => {
        if (res?.is_success) {
          console.log('Successfully Deleted Job')
          // Call getAllStoreIndexes to get the most updated data
          this.getAllStoreIndexes(true)
        } else {
          // Hide loading overlay
          this.gridApi.hideOverlay()
        }
      })
    }
  }

  onOpenOutputModal (data: any) {
    console.log('Open Output Modal: ', data, this.selectedMasterRows)
    let selectedDetailNodes = []
    this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
      selectedDetailNodes = [...info.api.getSelectedNodes()]
    })
    console.log('Open Output Modal: ', selectedDetailNodes)
    if (!data) data = this.selectedMasterRows[0] || selectedDetailNodes[0]
    const masterRows = cloneDeep(this.selectedMasterRows)
    const detailRows = cloneDeep(this.selectedDetailRows)
    const index_ids = Array.from(
      new Set([
        ...masterRows.flatMap(row =>
          row.data.detail_data.map(detail => detail.index_id).filter(id => id != null)
        ),
        ...detailRows.map(row => row.index_id).filter(id => id != null)
      ])
    )

    // Save Grid State
    SaveGridState(this.gridApi, this.gridOptions, this.gridName)
    // Open the modal
    this.ref = this.dialogService.open(OutputGridDataModalComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      contentStyle: { overflow: 'auto' },
      styleClass: 'large-modal',
      data: {
        componentParent: this,
        rowData: data || this.selectedMasterRows,
        index_ids
      }
    })
    // Modal Close Data
    this.ref.onClose.subscribe(data => {
      // Allow new api calls once the modal is closed
      this.triggerApiCallsService.onTriggerApiCalls({
        clear_api_calls: false
      })

      if (data) {
        console.log('Output Store Index Job Modal Closed: ', data)
        // Call getAllStoreIndexes to get the most updated data
        this.getAllStoreIndexes(true)
      }
    })
  }

  onOpenCreateEditJobsModal (data: any, action: string) {
    console.log('open modal', data, action)
    // Save Grid State
    SaveGridState(this.gridApi, this.gridOptions, this.gridName)
    // Open the modal
    this.ref = this.dialogService.open(CreateEditStoreIndexJobModalComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'large-modal',
      data: {
        componentParent: this,
        rowData: cloneDeep(data),
        action
      }
    })
    // Modal Close Data
    this.ref.onClose.subscribe(data => {
      // Allow new api calls once the modal is closed
      this.triggerApiCallsService.onTriggerApiCalls({
        clear_api_calls: false
      })
      if (data) {
        console.log('Create/Edit Store Index Job Modal Closed: ', data)
        this.getAllStoreIndexes(true)
      }
    })
  }

  onOpenCopyJobsModal (event) {
    const masterRows = cloneDeep(this.selectedMasterRows).map(row => row.data)
    this.gridApi.forEachDetailGridInfo(info => {
      info.api.getSelectedRows().forEach(row => {
        const newRow = this.gridApi.getRowNode(info.id.slice(7)).data
        const newRowJob = newRow.job_name
        let exists = false
        for (let i = 0; i < masterRows.length; i++) {
          const existingName = masterRows[i].job_name
          if (existingName == newRowJob) {
            exists = true
            break
          }
        }
        if (!exists) masterRows.push(newRow)
      })
    })

    // Save Grid State
    SaveGridState(this.gridApi, this.gridOptions, this.gridName)
    // Open the modal
    this.ref = this.dialogService.open(CopyStoreIndexJobModalComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'medium-short-modal',
      data: {
        componentParent: this,
        rowData: Array.from(new Set(masterRows))
      }
    })
    // Modal Close Data
    this.ref.onClose.subscribe(data => {
      // Allow new api calls once the modal is closed
      this.triggerApiCallsService.onTriggerApiCalls({
        clear_api_calls: false
      })
      if (data) {
        console.log('Copy Store Index Job Modal Closed: ', data)
        this.getAllStoreIndexes(true)
      }
    })
  }

  // Get all store indexes
  async getAllStoreIndexes (resetGridData: boolean) {
    // try {
    if (resetGridData) {
      this.gridApi.deselectAll()
      this.gridApi.setGridOption('rowData', [])
      this.selectedStoreIndexes = []
      this.gridApi.showLoadingOverlay()
    }
    // } finally {
    await this.storeIndexAPIs.GetAllStoreIndexes().then(res => {
      // Render the grid
      if (res && res.length > 0) {
        console.log('Store Indexes: ', res)
        this.renderGrid(this.storeIndex[0], this.storeIndex[1], res)
      } else if (res.length === 0) {
        this.gridApi.setGridOption('rowData', [])
      } else if (resetGridData) {
        this.gridApi.hideOverlay()
      }
    })
    // }
  }

  @HostListener('unloaded')
  ngOnDestroy () {
    this.routerSubscription?.unsubscribe()
    document.removeEventListener('contextmenu', this.onContextMenu)
    this.triggerApiCallsService.onTriggerApiCalls({
      clear_api_calls: true
    })

    if (this.gridApi) {
      this.gridApi.flushAsyncTransactions()
      this.gridApi.expireValueCache()
      this.gridApi = null
    }
  }
}
